<script lang="ts" setup>
import { Capacitor } from '@capacitor/core';
import { useAsyncState, useCurrentElement } from '@vueuse/core';
import { ModalElement } from 'inclusive-elements';
import { computed, shallowRef, watch } from 'vue';

import Dialog from '../../shared/components/Dialog.vue';
import Emojify from '../../shared/components/Emojify.vue';
import SomethingWentWrong from '../../shared/components/SomethingWentWrong.vue';
import IconCircleCheck from '../../shared/icons/IconCircleCheck.ts';
import { Product } from '../../shared/models/product.ts';
import { useActor } from '../../shared/stores/global.ts';
import { formatNumberFifty } from '../../shared/utils/format.ts';
import {
    RevenueCatPackage,
    revenueCatPackageMonths,
    usePaymentsProvider,
} from '../utils/payments.ts';
import ProductIcon from './ProductIcon.vue';

const props = defineProps<{ product: Product; message?: string }>();

const actor = useActor();
const paymentsProvider = usePaymentsProvider();

const { state: packages, isLoading } = useAsyncState(
    () => paymentsProvider.getPackages(props.product.offering || ''),
    undefined,
);

const selectedPackage = shallowRef<RevenueCatPackage | null>(null);

watch(packages, () => {
    selectedPackage.value = packages.value?.[packages.value.length - 1] || null;
});

function currency(amount: number) {
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: packages.value?.[0].currency,
        minimumFractionDigits: 0,
    }).format(amount);
}

function packageMonths(pkg: RevenueCatPackage) {
    return revenueCatPackageMonths[pkg.identifier] || 1;
}

function perMonthPrice(pkg: RevenueCatPackage) {
    return currency(pkg.price / packageMonths(pkg));
}

function savingsPercent(pkg: RevenueCatPackage, basePkg: RevenueCatPackage) {
    const baseMonthly = basePkg.price / packageMonths(basePkg);
    const pkgMonthly = pkg.price / packageMonths(pkg);
    return Math.round((1 - pkgMonthly / baseMonthly) * 100);
}

const el = useCurrentElement<ModalElement>();

const buttonLabel = computed(() => {
    if (!selectedPackage.value) return 'Continue';

    const months = packageMonths(selectedPackage.value);
    const total = currency(selectedPackage.value.price);

    return `Get ${months} month${months === 1 ? '' : 's'} for ${total}`;
});

const { execute, isLoading: isPurchasing } = useAsyncState(
    async () => {
        if (
            selectedPackage.value &&
            (await paymentsProvider.purchase(selectedPackage.value, props.product))
        ) {
            el.value.close();
        }
    },
    null,
    { immediate: false },
);

const isNative = Capacitor.isNativePlatform();

const features = computed(() => {
    const platformFeatures = Capacitor.isNativePlatform() ? [] : ['iOS, Android, and Web apps'];
    const { terms } = props.product;

    if (props.product.config?.subscriptionFeatures) {
        return [...props.product.config.subscriptionFeatures, ...platformFeatures];
    }

    const features = [];

    const qbankCount = props.product.qbank?.itemCount || 0;
    if (qbankCount) {
        features.push(`${formatNumberFifty(qbankCount)}+ MCQs with detailed explanations`);
    }

    const stationCount = props.product.stations?.itemCount || 0;
    if (stationCount) {
        features.push(`${formatNumberFifty(stationCount)}+ ${terms.stations} with marking sheets`);
    }

    const tutorialCount = props.product.tutorials?.itemCount || 0;
    if (tutorialCount) {
        features.push(`${formatNumberFifty(tutorialCount)}+ ${terms.tutorials}`);
    }

    if (props.product.hasExams) {
        features.push('Unlimited Mock Exam attempts');
    }

    features.push('High-yield learning points');
    features.push('Content frequently added and updated');
    features.push(...platformFeatures);

    return features;
});
</script>

<template>
    <ui-modal open @close="$emit('close')">
        <Dialog
            class="SubscribeDialog Dialog--large bg-fill-solid"
            :style="{
                backgroundImage: `radial-gradient(80% 500px at 50% 0%, ${product.color}55, transparent)`,
            }"
        >
            <div class="Dialog-body pt-0 pb-safe stack gap-xl">
                <div
                    v-if="message"
                    class="rounded p-md bg-warning-soft color-warning weight-bold text-center"
                >
                    {{ message }}
                </div>

                <div v-if="isLoading" class="spinner spinner--block" />

                <div v-else-if="packages" class="stack gap-xl">
                    <div class="content text-center">
                        <h1 class="row wrap gap-x-sm justify-center">
                            Subscribe to
                            <span> <ProductIcon :product /> {{ product.name }} </span>
                        </h1>

                        <p class="lead measure-narrow push-center">
                            {{ product.description }}
                        </p>
                    </div>

                    <div class="SubscribeDialog-columns grid gap-xl align-start">
                        <ul class="stack gap-sm" role="list">
                            <li v-for="feature in features" class="row align-start gap-xxs">
                                <IconCircleCheck class="icon text-md color-muted" />
                                {{ feature }}
                            </li>
                        </ul>

                        <div class="stack gap-md full-width" style="max-width: 23rem">
                            <div class="stack gap-xs">
                                <label
                                    v-for="(pkg, i) in packages"
                                    class="choice row py-sm px-sm gap-sm rounded align-start"
                                    :class="
                                        pkg === selectedPackage ? 'bg-primary-soft' : 'color-muted'
                                    "
                                    style="border: 2px solid"
                                    :style="{
                                        borderColor:
                                            selectedPackage === pkg
                                                ? 'var(--color-primary)'
                                                : 'var(--color-stroke)',
                                    }"
                                    @click="selectedPackage = pkg"
                                >
                                    <input type="radio" :checked="selectedPackage === pkg" />
                                    <span class="stack align-start">
                                        <span class="weight-bold">
                                            {{ revenueCatPackageMonths[pkg.identifier] }}
                                            month{{
                                                revenueCatPackageMonths[pkg.identifier] === 1
                                                    ? ''
                                                    : 's'
                                            }}
                                        </span>
                                        <span class="text-xs color-muted">
                                            {{ perMonthPrice(pkg) }} / month
                                        </span>
                                    </span>

                                    <span
                                        v-if="
                                            i > 0 &&
                                            packages[0] &&
                                            savingsPercent(pkg, packages[0]) > 0
                                        "
                                        class="badge push-end text-xs"
                                        :class="{
                                            'bg-primary': pkg === selectedPackage,
                                        }"
                                    >
                                        Save
                                        {{ savingsPercent(pkg, packages[0]) }}%
                                    </span>
                                </label>
                            </div>

                            <div
                                v-if="actor.freeMonth && product.referralProgram"
                                class="card p-md row gap-sm align-start"
                            >
                                <Emojify><span>🎁</span></Emojify>
                                <div class="stack gap-xs text-xs">
                                    <h6>Subscribe to claim your referral bonus</h6>
                                    <p>We'll add your extra month once you subscribe.</p>
                                </div>
                            </div>

                            <template v-if="selectedPackage && !isNative">
                                <div
                                    v-for="message in selectedPackage.messages"
                                    class="rounded p-md text-xs text-center"
                                    :style="message.style"
                                    v-html="message.html"
                                />
                            </template>

                            <div
                                class="sticky-bottom -my-md py-md stack gap-xs"
                                style="--sticky-soft-bleed: var(--dialog-gutter)"
                                v-sticky-observer
                            >
                                <button
                                    class="btn glass btn--lg bg-primary full-width"
                                    @click="execute()"
                                    :disabled="isPurchasing || !selectedPackage"
                                    :class="{ 'is-loading': isPurchasing }"
                                >
                                    {{ buttonLabel }}
                                </button>

                                <p class="text-xxs color-muted text-center">
                                    One-off payment, no auto-renewal. Extend anytime.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div v-if="product.config?.subscriptionFaqs?.length" class="stack gap-sm">
                        <details v-for="faq in product.config?.subscriptionFaqs" class="faq">
                            <summary class="weight-bold">
                                {{ faq.question }}
                            </summary>
                            <div class="content" v-html="faq.answer" />
                        </details>
                    </div>
                </div>

                <SomethingWentWrong v-else />
            </div>
        </Dialog>
    </ui-modal>
</template>

<style lang="less" scoped>
// Phones keep the usual sheet gutter, to leave the content its full width.
@media @query-tablet-up {
    .SubscribeDialog {
        --dialog-gutter: var(--space-xl);
    }
}

// The features sit beside the packages whenever both have room, rather than at
// a breakpoint, as the dialog's width varies. The minimum fits the purchase
// button, whose label won't wrap.
.SubscribeDialog-columns {
    --grid-min: 17rem;
    --grid-auto-repeat: auto-fit;
    justify-items: center;
}
</style>
